import PerformanceComparisonBase from '../valueObjects/comparisonData/PerformanceComparisonBase.js';
import * as Enums from '../enums/index.js';
import { ChartSeries, Series, BaseballResultsProcessor } from '../entities/index.js';
import FilteredBattingResultsProvider from './FilteredBattingResultsProvider.js';
import { BattingStatsCalculator, UniversalBattingStatsCalculator } from '../calculators/index.js'

class BattersPerformanceSeriesProvider
{
    constructor(maxValues, parameters, customResultsProcessor, filteredBattingResultsProvider, battingStatsCalculator) {
        this.resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();
        this.filteredBattingResultsProvider = filteredBattingResultsProvider || new FilteredBattingResultsProvider(this.resultsProcessor);
        this.battingStatsCalculator = battingStatsCalculator || new UniversalBattingStatsCalculator();

        if (!(this.filteredBattingResultsProvider instanceof FilteredBattingResultsProvider)) {
            throw new TypeError('filteredBattingResultsProvider must be an instance inheriting from FilteredBattingResultsProvider class');
        }

        if (!(this.resultsProcessor instanceof BaseballResultsProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from BaseballResultsProcessor class');
        }

        if (!(maxValues instanceof PerformanceComparisonBase)) {
            throw new TypeError('maxValues must be an instance inheriting from PerformanceComparisonBase class');
        }

        if (!(this.battingStatsCalculator instanceof BattingStatsCalculator)) {
            throw new TypeError('battingStatsCalculator must be an instance inheriting from BattingStatsCalculator class');
        }

        if (typeof(parameters) === 'undefined') {
            parameters = [
                Enums.HitData.ExitSpeed.key, Enums.HitData.CarryDistance.key,
                Enums.HitData.LaunchV.key,
                Enums.BattingStats.HardHitRate.key,
                // Enums.HitData.LaunchH.key,
            ];
        }
        else if (!(parameters instanceof Array)) {
            throw new TypeError('parameters must be an array containing name of data parameters');
        }

        this.maxValues = maxValues;
        this.parameters = parameters;
    }

    getRelativeValue(stats, maxValues, pitchType, parameterName, statType, relativeStatType) {
        const value = {
            [statType]: NaN,
            [relativeStatType]: NaN,
            percent: NaN
        };

        let found = 0;

        if (stats.hasOwnProperty(parameterName) && stats[parameterName].hasOwnProperty(statType)) {
            value[statType] = stats[parameterName][statType];
            found++;
        }

        if (maxValues.hasStat(pitchType, parameterName, relativeStatType)) {
            value[relativeStatType] = maxValues.getStat(pitchType, parameterName, relativeStatType);
            found++;
        }

        if (found === 2) {
            value.percent = Math.abs(stats[parameterName][statType] * 100 / maxValues.getStat(pitchType, parameterName, relativeStatType));
        }

        return value;
    }

    getRelativeComparisonValue(comparisonValue, maxValues, pitchType, parameterName, statType, relativeStatType) {
        const refName = 'ref-' + relativeStatType;
        const value = {
            [statType]: NaN,
            [refName]: NaN,
            percent: NaN
        };

        let found = 0;

        if (comparisonValue.hasStat(pitchType, parameterName, statType)) {
            value[statType] = maxValues.getStat(pitchType, parameterName, statType);
            found++;
        }

        if (maxValues.hasStat(pitchType, parameterName, relativeStatType)) {
            value[refName] = maxValues.getStat(pitchType, parameterName, relativeStatType);
            found++;
        }

        if (found === 2) {
            value.percent = Math.abs(maxValues.getStat(pitchType, parameterName, statType) * 100 / maxValues.getStat(pitchType, parameterName, relativeStatType));
        }

        return value;
    }

    defaultFilters() {
        return this.filteredBattingResultsProvider.defaultFilters();
    }

    includePerformanceStats(stats, filteredPairs) {
        if (!this.parameters.includes(Enums.BattingStats.HardHitRate.key)){
            return;
        }

        const batterStats = this.battingStatsCalculator.calculateHitterStats(filteredPairs);

        stats[Enums.BattingStats.HardHitRate.key] = {
            [Enums.StatType.Avg.key]: batterStats[Enums.BattingStats.HardHitRate.key],
            [Enums.StatType.StDev.key]: 0,
        };
    }

    getData(
        results,
        {
            // tagging context
            pitchType, pitcherIds, pitcherHandedness, pitchResults, batterIds, batterHandedness,
            // scoring context
            outs, balls, strikes, runnersOn,
        } = {},
        comparisonData
    ) {
        if (typeof(pitchType) === 'undefined') {
            throw new TypeError('pitchType must be defined');
        }

        if (!(results instanceof Array)) {
            throw new TypeError('results must be an array');
        }

        if (typeof(comparisonData) === 'undefined') {
            comparisonData = [];
        }
        else if (!(comparisonData instanceof Array)) {
            throw new TypeError('comparisonData must be an array containing instances inheriting from PerformanceComparisonBase class');
        }
        else if (comparisonData.some(c => !(c instanceof PerformanceComparisonBase))) {
            throw new TypeError('comparisonData can contain only instances inheriting from PerformanceComparisonBase class');
        }

        const filteredPairs = this.filteredBattingResultsProvider.filterResults(results, {
            // tagging context
            pitcherIds, pitcherHandedness, pitchResults, pitchTypes: [pitchType], batterIds, batterHandedness,
            // scoring context
            outs, balls, strikes, runnersOn,
        });

        const filteredHits = this.filteredBattingResultsProvider.extractHits(filteredPairs);

        // get averages for player
        let stats = this.resultsProcessor.calcStats([Enums.StatType.Avg.key, Enums.StatType.StDev.key], filteredHits, 
        // TODO - simplify it...
        [ [ [], 'Data', this.parameters ] ], false);

        const chartSeries = new ChartSeries(undefined, this.parameters);
        const dataSeries = [];

        // get series with data from results
        if (stats) {
            this.includePerformanceStats(stats, filteredPairs);
            for (let i = 0; i < this.parameters.length; i++) {
                dataSeries.push(
                    this.getRelativeValue(stats, this.maxValues, null, this.parameters[i], Enums.StatType.Avg.key, Enums.StatType.Max.key)
                );
            }
        }

        chartSeries.push(new Series('Results', 1, dataSeries));

        // get data for comparison
        for (let c = 0; c < comparisonData.length; c++) {
            const compareTo = comparisonData[c];
            const comparisonSeries = [];
    
            for (let i = 0; i < this.parameters.length; i++) {
                comparisonSeries.push(
                    this.getRelativeComparisonValue(compareTo, this.maxValues, null, this.parameters[i], Enums.StatType.Avg.key, Enums.StatType.Max.key)
                );
            }

            chartSeries.push(new Series(compareTo.name, 1, comparisonSeries));
        }

        return chartSeries;
    }
}

export default BattersPerformanceSeriesProvider;